import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { AppConfigProvider } from '@app/app.config.provider';
import { NotificationComponent } from '@components/notification/notification.component';
import { INotification, INotificationQuery } from '@models/notification/notification';
import { IProduct } from '@models/product';
import { OpenIDConnectService } from '@services/auth/openid-connect.service';
import { MenuService } from '@services/menu.service';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProductService } from './product.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiNotifications: string;
  private apiNotificationsStream: string;
  private products: IProduct[];
  private streamSource: EventSource = null;
  private streamTimer: NodeJS.Timeout = null;
  private readonly streamTimeout = 5000;
  private readonly streamTimeoutLong = 30000;

  private readonly notifications$ = new BehaviorSubject<INotification[]>(null);

  get notificationListener() {
    return this.notifications$.asObservable();
  }

  get notifications() {
    return this.notifications$.getValue();
  }

  set notifications(notifications: INotification[]) {
    this.notifications$.next(notifications);
  }

  constructor(
    private readonly appConfig: AppConfigProvider,
    private readonly auth: OpenIDConnectService,
    private readonly dialog: MatDialog,
    private readonly http: HttpClient,
    private readonly menuService: MenuService,
    private readonly productService: ProductService
  ) {
    this.appConfig.environment.subscribe(env => {
      this.apiNotifications = `${env.API.REFERENCE_DATA}/api/notifications`;
      this.apiNotificationsStream = `${env.API.REFERENCE_DATA}/api/notifications/notifications-stream`;
    });

    this.productService
      .getProducts()
      .toPromise()
      .then(async products => {
        this.products = products;
        // Little head start while lists loads
        this.notifications = await this.getNotifications(null, null);
        this.connectToNotificationsStream();
      });
  }

  /**
   * Gets notifications by product and by Calibration type
   * if either input is left null will search all
   *
   * When request is complete will add records to the notifications$ behavior subject
   */
  public getNotifications(ProductId: number, CalibrationType: string): Promise<INotification[]> {
    const query: INotificationQuery = { ProductId, CalibrationType };
    return this.http
      .post<INotification[]>(`${this.apiNotifications}`, query)
      .pipe(
        map(notifications => {
          this.notifications = this.parseNotifications(notifications);
          return this.notifications;
        })
      )
      .toPromise();
  }

  public connectToNotificationsStream(): void {
    try {
      const user = this.auth.user;
      const { expired, expires_in, token_type, access_token } = user;
      const isIdling = this.auth.isIdling;

      if (this.streamSource) {
        this.streamSource.close();
        this.streamSource = null;
      }

      if (expired || isIdling || !user || expires_in < 5) {
        clearTimeout(this.streamTimer);
        this.streamTimer = setTimeout(() => this.connectToNotificationsStream(), this.streamTimeout);
        return;
      }

      this.streamSource = new EventSourcePolyfill(this.apiNotificationsStream, {
        headers: { Authorization: `${token_type} ${access_token}` }
      });

      this.streamSource.addEventListener('error', (errorEvent: ErrorEvent) => {
        this.streamSource.close();
        const failedToFetch = errorEvent.error && errorEvent.error.message === 'Failed to fetch';
        clearTimeout(this.streamTimer);
        this.streamTimer = setTimeout(
          () => this.connectToNotificationsStream(),
          failedToFetch ? this.streamTimeoutLong : this.streamTimeout
        );
      });

      this.streamSource.addEventListener('notifications', (message: MessageEvent) => {
        try {
          if (message.data) {
            const notifications = JSON.parse(message.data) as INotification[];
            this.notifications = this.parseNotifications(notifications);
          }
        } catch (error) {
          console.warn(`Error parsing notifications from stream`, error);
        }
      });
    } catch (error) {
      console.error(`Error connecting to Notification stream`, error);
    }
  }

  /**
   * Parses the notification list adding optional fields like
   * ProductName and ProductMnemonic.
   *
   * Sorts the list by ProductMnemonic and then calibration type
   * @param List of notifications
   * @returns List of notifications
   */
  private parseNotifications(notifications: INotification[]) {
    // Adding additional fields
    notifications.forEach(notification => {
      notification.DateTimeCreated = new Date(notification.DateTimeCreated);
      const product = this.products.find(p => p.ProductId === notification.ProductId);
      notification.ProductName = product ? product.ProductName : `Product Id ${notification.ProductId}`;
      notification.ProductMnemonic = product ? product.Mnemonic : `Product Id ${notification.ProductId}`;
    });

    const productMnemonics = new Set<string>();

    notifications.sort((a, b) => a.ProductMnemonic.localeCompare(b.ProductMnemonic)).forEach(x => productMnemonics.add(x.ProductMnemonic));

    let sortedNotifications: INotification[] = [];

    productMnemonics.forEach(productMnemonic => {
      sortedNotifications = sortedNotifications.concat(
        notifications.filter(x => x.ProductMnemonic === productMnemonic).sort((a, b) => a.CalibrationType.localeCompare(b.CalibrationType))
      );
    });

    return sortedNotifications;
  }

  public openNotificationWindow() {
    this.dialog.open(NotificationComponent, {
      data: {
        notifications:
          this.menuService.selectedProduct.ProductId !== null && this.menuService.selectedOption.group === 'product'
            ? this.notifications.filter(x => x.ProductId === this.menuService.selectedProduct.ProductId)
            : this.notifications
      },
      disableClose: true
    });
  }
}
